import type { PricingCampaignModel } from '@/shared/generated'

import { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { any, prop } from 'lodash/fp'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useWithAffectedOGsHandler } from '@/entities/optimization-group'
import {
	getGlobalPricingCampaigns,
	getLocaPricingCampaigns,
	orderCampaigns,
	usePricingCampaignsQuery,
	useReorderPricingCampaignsMutation,
} from '@/entities/pricing-campaign'
import { useScenarioQuery } from '@/entities/scenario'

import { ampli } from '@/shared/lib/ampli'
import Sentry from '@/shared/lib/sentry'
import {
	actSaved,
	errorMessage,
	processingMessage,
	successActionMessage,
} from '@/shared/tools/message'

import { getGlobalPricingCampaignsByPcSet } from './getGlobalPricingCampaignsByPcSet'

type OrderingFormData = {
	global: PricingCampaignModel[]
	local: PricingCampaignModel[]
}
export function usePricingCampaignOrdering({
	optimizationGroupId,
	scenarioId,
	globalPcSetId,
}: {
	optimizationGroupId?: string | null
	scenarioId?: string | null
	globalPcSetId?: string | null
}) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const isGlobal = !optimizationGroupId && Boolean(globalPcSetId)
	const scenarioQuery = useScenarioQuery(scenarioId!, {
		enabled: !!scenarioId,
	})
	const queryParams = isGlobal
		? /**
			 * Global scope: returns both active and simulated (what-if) campaigns across non-main sets
			 */
			{
				only_global: true,
				only_main_global: false,
			}
		: scenarioId
			? {
					scenario_id: scenarioId,
					optimization_group_id: optimizationGroupId,
				}
			: { optimization_group_id: optimizationGroupId }
	const pricingCampaignsQuery = usePricingCampaignsQuery(queryParams)
	const baseReorderMutation = useReorderPricingCampaignsMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
		},
	})
	const reorderWith424Handler = useWithAffectedOGsHandler(baseReorderMutation)
	const reorderMutation = reorderWith424Handler
	const isProcessing = any(prop('isPending'), [reorderMutation])
	const isLoading = any(prop('isLoading'), [
		pricingCampaignsQuery,
		scenarioQuery,
	])
	const formData = useMemo(() => {
		const data = pricingCampaignsQuery.data || []

		return {
			global: orderCampaigns(
				isGlobal
					? getGlobalPricingCampaignsByPcSet(globalPcSetId!, data)
					: getGlobalPricingCampaigns(data)
			),
			local: orderCampaigns(getLocaPricingCampaigns(data)),
		}
	}, [pricingCampaignsQuery.data, isGlobal, globalPcSetId])
	const methods = useForm<OrderingFormData>({
		defaultValues: formData ?? undefined,
	})
	useEffect(() => {
		methods.reset(formData)
	}, [formData])
	const _handleLocalOrdering = async ({ local }: OrderingFormData) => {
		ampli.pcReorderPrioritySave({
			pcs_type: 'local',
		})
		toast.promise(
			reorderMutation.mutateAsync({
				scope: 'scenario',
				scenario_id: scenarioId!,
				pricing_campaigns: local.map(({ id, order }) => ({ id, order })),
			}),
			{
				loading: processingMessage(t),
				success: successActionMessage(t('general_priority'), actSaved(t), t),
				error: (error) => {
					Sentry.captureException(error)
					if (error instanceof AxiosError) {
						return `${t('toast.entity.failed', { entity: t('entity.pricing_campaign') })} "${error.response?.data.detail}"`
					}

					return errorMessage(t)
				},
			}
		)
	}
	const _handleGlobalOrdering = async ({ global }: OrderingFormData) => {
		ampli.pcReorderPrioritySave({
			pcs_type: 'global',
		})
		/**
		 * try to reorder
		 * - if it fails with 424, show the modal
		 * - if it fails with non-424 error, show the error toast
		 * - if it succeeds, show the success toast
		 * @note We dont use toast promise here because we want to handle the error manually
		 * @note We dont pass onSuccess callback to the mutation because the target mutation excuted on the firts step.
		 */
		try {
			await reorderMutation.mutateAsync({
				scope: 'global_pc_set',
				global_pc_set_id: globalPcSetId!,
				pricing_campaigns: global.map(({ id, order }) => ({ id, order })),
			})
			toast.success(successActionMessage(t('general_priority'), actSaved(t), t))
		} catch (error) {
			if (error instanceof AxiosError) {
				if (error.status === 424) {
					return
				} else {
					toast.error(
						`${t('toast.entity.failed', { entity: t('entity.pricing_campaign') })} "${error.response?.data.detail}"`
					)
				}
			}
			Sentry.captureException(error)
		}
	}
	const handleSubmit = isGlobal
		? methods.handleSubmit(_handleGlobalOrdering)
		: methods.handleSubmit(_handleLocalOrdering)
	const isError = useMemo(
		() => reorderMutation.error && reorderMutation.error.status !== 424,
		[reorderMutation.error]
	)

	return {
		methods,
		handleSubmit,
		isProcessing,
		isLoading,
		isError,
	}
}
